import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../utils/api";
import { API_ENDPOINTS } from "../../constants/api";
import MediaComponent from "../MediaComponent";
import InlineSlideUpload from "./InlineSlideUpload";
import "./ClientSlidesView.css";

function ClientSlidesView({ client, onBack, onClientUpdate }) {
  const [editingBrief, setEditingBrief] = useState(false);
  const [briefValue, setBriefValue] = useState(client.client_brief || "");
  const [editingSlide, setEditingSlide] = useState(null); // slide ID being edited
  const [slideDescriptions, setSlideDescriptions] = useState(
    client.client_slides?.reduce((acc, slide) => {
      acc[slide._id] = slide.description || "";
      return acc;
    }, {}) || {}
  );
  const [updating, setUpdating] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(null); // slide ID being confirmed for deletion
  const [slidesOrder, setSlidesOrder] = useState(client.client_slides || []); // Track current slide order
  const [orderChanged, setOrderChanged] = useState(false); // Track if order has been modified

  // Sync slidesOrder when client prop changes
  useEffect(() => {
    setSlidesOrder(client.client_slides || []);
    setOrderChanged(false);
  }, [client.client_slides]);

  // Update client brief
  const handleUpdateBrief = async () => {
    setUpdating(true);
    try {
      // Create updated client object
      const updatedClient = {
        ...client,
        client_brief: briefValue.trim(),
      };

      // Send whole client object to backend
      const data = {
        client_slides: updatedClient.client_slides,
        client_name: updatedClient.client_name,
        client_brief: updatedClient.client_brief,
      };

      const response = await api.post(
        API_ENDPOINTS.CLIENTS.CREATE_UPDATE,
        data
      );

      if (response.data.success) {
        toast.success("Client brief updated successfully!");
        setEditingBrief(false);

        // Update parent component with new client data
        onClientUpdate(updatedClient);
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating client brief:", error);
      toast.error(
        error.response?.data?.message || "Failed to update client brief"
      );
    } finally {
      setUpdating(false);
    }
  };

  // Update slide description
  const handleUpdateSlideDescription = async (slideId) => {
    setUpdating(true);
    try {
      const description = slideDescriptions[slideId];

      // Create updated client object with modified slide
      const updatedSlides = client.client_slides.map((slide) =>
        slide._id === slideId
          ? { ...slide, description: description.trim() }
          : slide
      );
      const updatedClient = {
        ...client,
        client_slides: updatedSlides,
      };

      // Send whole client object to backend
      const data = {
        client_slides: updatedClient.client_slides,
        client_name: updatedClient.client_name,
        client_brief: updatedClient.client_brief,
      };

      const response = await api.post(
        API_ENDPOINTS.CLIENTS.CREATE_UPDATE,
        data
      );

      if (response.data.success) {
        toast.success("Slide description updated successfully!");
        setEditingSlide(null);

        // Update parent component with new client data
        onClientUpdate(updatedClient);
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating slide description:", error);
      toast.error(
        error.response?.data?.message || "Failed to update slide description"
      );
    } finally {
      setUpdating(false);
    }
  };

  // Cancel brief editing
  const handleCancelBriefEdit = () => {
    setBriefValue(client.client_brief || "");
    setEditingBrief(false);
  };

  // Cancel slide description editing
  const handleCancelSlideEdit = (slideId) => {
    setSlideDescriptions((prev) => ({
      ...prev,
      [slideId]:
        client.client_slides.find((s) => s._id === slideId)?.description || "",
    }));
    setEditingSlide(null);
  };

  // Delete slide
  const handleDeleteSlide = async (slideId) => {
    setUpdating(true);
    try {
      // Create updated client object with slide removed
      const updatedSlides = client.client_slides.filter(
        (slide) => slide._id !== slideId
      );
      const updatedClient = {
        ...client,
        client_slides: updatedSlides,
      };

      // Send whole client object to backend
      const data = {
        client_slides: updatedClient.client_slides,
        client_name: updatedClient.client_name,
        client_brief: updatedClient.client_brief,
      };

      const response = await api.post(
        API_ENDPOINTS.CLIENTS.CREATE_UPDATE,
        data
      );

      if (response.data.success) {
        toast.success("Slide deleted successfully!");
        setConfirmingDelete(null);

        // Update parent component with new client data
        onClientUpdate(updatedClient);
      } else {
        throw new Error(response.data.message || "Delete failed");
      }
    } catch (error) {
      console.error("Error deleting slide:", error);
      toast.error(error.response?.data?.message || "Failed to delete slide");
    } finally {
      setUpdating(false);
    }
  };

  // Cancel delete confirmation
  const handleCancelDelete = () => {
    setConfirmingDelete(null);
  };

  // Move slide left (decrease index)
  const handleMoveSlideLeft = (slideId) => {
    const currentIndex = slidesOrder.findIndex(
      (slide) => slide._id === slideId
    );
    if (currentIndex > 0) {
      const newOrder = [...slidesOrder];
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = [
        newOrder[currentIndex],
        newOrder[currentIndex - 1],
      ];
      setSlidesOrder(newOrder);
      setOrderChanged(true);
    }
  };

  // Move slide right (increase index)
  const handleMoveSlideRight = (slideId) => {
    const currentIndex = slidesOrder.findIndex(
      (slide) => slide._id === slideId
    );
    if (currentIndex < slidesOrder.length - 1) {
      const newOrder = [...slidesOrder];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [
        newOrder[currentIndex + 1],
        newOrder[currentIndex],
      ];
      setSlidesOrder(newOrder);
      setOrderChanged(true);
    }
  };

  // Save new slide order to backend
  const handleSaveOrder = async () => {
    setUpdating(true);
    try {
      // Create updated client object with new slide order
      const updatedClient = {
        ...client,
        client_slides: slidesOrder,
      };

      // Send whole client object to backend
      const data = {
        client_slides: updatedClient.client_slides,
        client_name: updatedClient.client_name,
        client_brief: updatedClient.client_brief,
      };

      const response = await api.post(
        API_ENDPOINTS.CLIENTS.CREATE_UPDATE,
        data
      );

      if (response.data.success) {
        toast.success("Slide order updated successfully!");
        setOrderChanged(false);

        // Update parent component with new client data
        onClientUpdate(updatedClient);
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating slide order:", error);
      toast.error(
        error.response?.data?.message || "Failed to update slide order"
      );
    } finally {
      setUpdating(false);
    }
  };

  // Reset slide order to original
  const handleResetOrder = () => {
    setSlidesOrder(client.client_slides || []);
    setOrderChanged(false);
  };

  return (
    <div className="client-slides">
      <div className="section-header">
        <button
          onClick={onBack}
          className="btn-back">
          ← Back to Clients
        </button>
        <h2>{client.client_name} - Slides Management</h2>
      </div>

      <div className="slides-container">
        <div className="client-brief">
          <div className="brief-section">
            <div className="brief-header">
              <h4>Client Brief</h4>
              {!editingBrief ? (
                <button
                  className="btn-edit-brief"
                  onClick={() => setEditingBrief(true)}
                  disabled={updating}>
                  ✏️ Edit
                </button>
              ) : (
                <div className="brief-edit-actions">
                  <button
                    className="btn-save"
                    onClick={handleUpdateBrief}
                    disabled={updating || !briefValue.trim()}>
                    {updating ? "Saving..." : "Save"}
                  </button>
                  <button
                    className="btn-cancel"
                    onClick={handleCancelBriefEdit}
                    disabled={updating}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
            {!editingBrief ? (
              <p className="brief-text">
                {client.client_brief ||
                  "No brief provided yet. Click Edit to add one."}
              </p>
            ) : (
              <textarea
                className="brief-textarea"
                value={briefValue}
                onChange={(e) => setBriefValue(e.target.value)}
                placeholder="Enter client brief..."
                rows={4}
                disabled={updating}
              />
            )}
          </div>
        </div>

        <div className="slides-list">
          {!client.client_slides || client.client_slides.length === 0 ? (
            <div className="empty-state">
              <h3>No Slides Found</h3>
              <p>
                This client doesn't have any slides yet. Upload their first
                slides to get started.
              </p>
            </div>
          ) : (
            <div className="slides-grid">
              {slidesOrder.map((slide, index) => (
                <div
                  key={slide._id || index}
                  className="slide-card">
                  <div className="slide-header">
                    <h4>Slide {index + 1}</h4>
                    <div className="slide-actions">
                      <button
                        className="btn-move-left"
                        title="Move Left"
                        onClick={() => handleMoveSlideLeft(slide._id)}
                        disabled={updating || index === 0}>
                        ←
                      </button>
                      <button
                        className="btn-move-right"
                        title="Move Right"
                        onClick={() => handleMoveSlideRight(slide._id)}
                        disabled={updating || index === slidesOrder.length - 1}>
                        →
                      </button>
                      <button
                        className="btn-delete"
                        title="Delete Slide"
                        onClick={() => setConfirmingDelete(slide._id)}
                        disabled={updating}>
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="slide-preview">
                    {slide.location && (
                      <div className="slide-thumbnail">
                        <MediaComponent
                          src={slide.location}
                          alt={slide.description || `Slide ${index + 1}`}
                          className="slide-media"
                          isPreview={true}
                          autoPlay={false}
                          controls={false}
                          muted={true}
                        />
                      </div>
                    )}
                  </div>
                  <div className="slide-info">
                    <div className="slide-description-section">
                      {editingSlide !== slide._id ? (
                        <div className="description-display">
                          <p className="slide-description">
                            {slide.description || "No description"}
                          </p>
                          <button
                            className="btn-edit-description"
                            onClick={() => setEditingSlide(slide._id)}
                            disabled={updating}>
                            ✏️
                          </button>
                        </div>
                      ) : (
                        <div className="description-edit">
                          <input
                            type="text"
                            className="description-input"
                            value={slideDescriptions[slide._id] || ""}
                            onChange={(e) =>
                              setSlideDescriptions((prev) => ({
                                ...prev,
                                [slide._id]: e.target.value,
                              }))
                            }
                            placeholder="Enter slide description..."
                            disabled={updating}
                          />
                          <div className="description-edit-actions">
                            <button
                              className="btn-save-small"
                              onClick={() =>
                                handleUpdateSlideDescription(slide._id)
                              }
                              disabled={
                                updating ||
                                !slideDescriptions[slide._id]?.trim()
                              }>
                              {updating ? "..." : "✓"}
                            </button>
                            <button
                              className="btn-cancel-small"
                              onClick={() => handleCancelSlideEdit(slide._id)}
                              disabled={updating}>
                              ✕
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="slide-location">
                      <small>Location: {slide.location || "N/A"}</small>
                    </p>

                    {/* Delete Confirmation */}
                    {confirmingDelete === slide._id && (
                      <div className="delete-confirmation">
                        <p>
                          Are you sure you want to delete this slide? This
                          action cannot be undone.
                        </p>
                        <div className="delete-confirmation-actions">
                          <button
                            className="btn-confirm-delete"
                            onClick={() => handleDeleteSlide(slide._id)}
                            disabled={updating}>
                            {updating ? "Deleting..." : "Yes, Delete"}
                          </button>
                          <button
                            className="btn-cancel-delete"
                            onClick={handleCancelDelete}
                            disabled={updating}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Slide Order Update Controls */}
              {orderChanged && (
                <div className="slide-order-controls">
                  <div className="order-message">
                    <p>
                      You have changed the slide order. Click "Save Order" to
                      update.
                    </p>
                  </div>
                  <div className="order-actions">
                    <button
                      className="btn-save-order"
                      onClick={handleSaveOrder}
                      disabled={updating}>
                      {updating ? "Saving..." : "Save Order"}
                    </button>
                    <button
                      className="btn-reset-order"
                      onClick={handleResetOrder}
                      disabled={updating}>
                      Reset
                    </button>
                  </div>
                </div>
              )}

              {/* Add More Slides Upload Card */}
              <InlineSlideUpload
                client={client}
                onUploadSuccess={onClientUpdate}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientSlidesView;
